import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardShell from '../../../layouts/DashboardShell';
import { fetchUserCv, rateJob } from '../../../services/api';
import { CvModal } from '../components/ClientApplicationJobCard';
import useClientCompletedJobs from '../../../features/jobs/hooks/useClientCompletedJobs';
import { clientSideLinks } from '../clientRoutes';
import CardIcon from '../../../shared/components/icons/CardIcon';

const money = (value) => `K${Number(value || 0).toLocaleString('en-ZM', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const date = (value) => value ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Date unavailable';
const workersFor = (job) => job.workers || [];
const nameFor = (worker) => `${worker.firstName || worker.worker?.firstName || ''} ${worker.middleName || worker.worker?.middleName || ''} ${worker.lastName || worker.worker?.lastName || ''}`.replace(/\s+/g, ' ').trim() || 'Worker';

function WorkerRow({ worker, job }) {
  const [score, setScore] = useState(worker.rating?.score ?? worker.ratingScore ?? 0);
  const [comment, setComment] = useState(worker.rating?.comment || '');
  const [ratingOpen, setRatingOpen] = useState(false);
  const [cv, setCv] = useState(null);
  const [busy, setBusy] = useState(false);
  const workerId = worker.worker?.id || worker.id;
  const existingScore = worker.rating?.score ?? worker.ratingScore;

  const submitRating = async () => {
    if (!score || !workerId) return;
    setBusy(true);
    try {
      await rateJob(job.id, { toUserId: workerId, score, comment: comment.trim() });
      toast.success('Worker rating saved.');
      setRatingOpen(false);
    } catch (error) {
      toast.error(error.message || 'Unable to save rating.');
    } finally {
      setBusy(false);
    }
  };

  const openCv = async () => {
    if (!workerId) return;
    setBusy(true);
    try {
      const data = await fetchUserCv(workerId);
      setCv({ data, worker });
    } catch (error) {
      toast.error(error.message || 'Unable to load CV.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="font-bold text-navy">{nameFor(worker)}</h3>
          <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-slate-600"><CardIcon name="location" />{[worker.area || worker.worker?.area, worker.city || worker.worker?.city].filter(Boolean).join(', ') || 'Location unavailable'}</p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700"><CardIcon name="check" />{worker.status || 'Completed'}</span>
      </div>
      <div className="mt-4 grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
        <p className="inline-flex items-center gap-1.5"><CardIcon name="money" />Amount: <strong>{money(worker.amount ?? worker.agreedAmount ?? worker.paymentAmount ?? job.budget / Math.max(workersFor(job).length, 1))}</strong></p>
        <p className="inline-flex items-center gap-1.5"><CardIcon name="calendar" />Completed: <strong>{date(worker.completedAt)}</strong></p>
      </div>
      <div className="mt-4 border-t border-slate-100 pt-4">
        <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500"><CardIcon name="star" />Your Rating</p>
        <p className="mt-2 inline-flex items-center gap-1">
          {existingScore == null ? 'Not rated yet' : [1, 2, 3, 4, 5].map((value) => <CardIcon key={value} name={value <= existingScore ? 'starFilled' : 'star'} className={`h-4 w-4 ${value <= existingScore ? 'text-orange-500' : 'text-slate-300'}`} />)}
          {worker.rating?.comment && <span className="text-sm text-slate-500"> · {worker.rating.comment}</span>}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" onClick={openCv} disabled={busy} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700"><CardIcon name="file" />{busy ? 'Loading...' : 'View CV'}</button>
          {existingScore == null && <button type="button" onClick={() => setRatingOpen(true)} className="inline-flex items-center gap-2 rounded-xl bg-navy px-3 py-2 text-sm font-semibold text-white"><CardIcon name="star" />Rate Worker</button>}
        </div>
      </div>
      {ratingOpen && (
        <div className="mt-4 rounded-2xl bg-slate-50 p-4">
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((value) => <button key={value} type="button" onClick={() => setScore(value)} className={`${value <= score ? 'text-orange-500' : 'text-slate-300'}`} aria-label={`${value} stars`}><CardIcon name="starFilled" className="h-7 w-7" /></button>)}
          </div>
          <textarea value={comment} onChange={(event) => setComment(event.target.value)} rows="2" placeholder="Add a comment (optional)" className="mt-3 w-full rounded-xl border border-slate-200 p-3 text-sm" />
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => setRatingOpen(false)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">Cancel</button>
            <button type="button" onClick={submitRating} disabled={!score || busy} className="rounded-xl bg-navy px-3 py-2 text-sm font-semibold text-white">Save rating</button>
          </div>
        </div>
      )}
      {cv && <CvModal cv={cv.data} applicant={{ applicant: cv.worker }} job={job} onClose={() => setCv(null)} hideShare />}
    </div>
  );
}

function CompletedJobDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();
  const { jobs, loading } = useClientCompletedJobs();
  const job = state?.job || jobs.find((item) => String(item.id) === String(id));

  if (!job && loading) return <DashboardShell role="CLIENT" sideLinks={clientSideLinks}><div className="rounded-3xl bg-white p-8 text-center text-slate-600">Loading completed job...</div></DashboardShell>;
  if (!job) return <DashboardShell role="CLIENT" sideLinks={clientSideLinks}><div className="rounded-3xl bg-white p-8 text-center text-slate-500">Completed job not found.</div></DashboardShell>;

  const workers = workersFor(job);
  const completedCount = job.completedWorkerCount ?? workers.filter((worker) => worker.status?.toLowerCase() === 'completed').length;
  const required = job.requiredPeople || 1;
  const location = [job.area, job.city].filter(Boolean).join(', ') || 'Location unavailable';

  return (
    <DashboardShell role="CLIENT" sideLinks={clientSideLinks}>
      <div className="space-y-6">
        <button type="button" onClick={() => navigate('/client/completed-jobs')} className="inline-flex items-center gap-2 rounded-xl bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"><CardIcon name="arrowLeft" />Completed Jobs</button>
        <header className="rounded-3xl bg-white p-6 shadow-sm">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700"><CardIcon name="check" />COMPLETED</span>
          <h1 className="mt-4 text-2xl font-bold text-navy">{job.title || 'Completed Job'}</h1>
          <p className="mt-2 text-sm leading-6 text-slate-600">{job.description || 'No description provided.'}</p>
          <div className="mt-5 grid gap-3 text-sm text-slate-700 sm:grid-cols-2">
            <p className="inline-flex items-center gap-1.5"><CardIcon name="location" />{location}</p>
            <p className="inline-flex items-center gap-1.5"><CardIcon name="money" />Budget: <strong>{money(job.budget)}</strong></p>
            <p className="inline-flex items-center gap-1.5"><CardIcon name="people" />{completedCount}/{required} workers completed</p>
            <p className="inline-flex items-center gap-1.5"><CardIcon name="calendar" />Updated {date(job.updatedAt)}</p>
          </div>
        </header>
        <section className="rounded-3xl bg-white p-6 shadow-sm">
          <h2 className="inline-flex items-center gap-2 text-lg font-bold text-navy"><CardIcon name="people" />Workers</h2>
          <div className="mt-4 space-y-4">{workers.map((worker) => <WorkerRow key={worker.id || worker.applicationId || nameFor(worker)} worker={worker} job={job} />)}</div>
        </section>
      </div>
    </DashboardShell>
  );
}

export default CompletedJobDetailsPage;
